import React, {useMemo, useRef, useState} from "react";
import {connect, Provider} from "react-redux";
import {Button, Typography, withStyles} from "@material-ui/core";
import RaspberryEraseStationList from "./Table/RaspberryEraseStationList";
import {createHistory, hasPermission} from "../../sc-utilities";
import { store } from './store/store';
import {FindInPage, GetApp, RotateLeft} from "@material-ui/icons";
import {isValid} from "../MobileEraseStation"
import * as raspberryEraseStationActions from "./store/raspberryEraseStationActions";
import {Redirect} from "react-router-dom";
import {snackActions} from "../../sc-utilities/SnackBar";

const styles = theme => ({
    "@global": {
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: 'none',
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='active']": {
            color: theme.palette.success.dark
        },
        "[class*='inactive']": {
            color: theme.palette.error.dark
        },
        '.MuiIconButton-colorInherit': {
            color: 'rgba(0, 0, 0, 0.54)',
        },
    },
    root: {
        width: `100%`,
        height: 'auto',
        paddingLeft: theme.spacing(-1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    active: {
        color: theme.palette.success.dark
    },
    inactive: {
        color: theme.palette.error.dark
    },
    remote: {
        textDecoration: 'none',
        color: 'white'
    },
});

const RaspberryEraseStation = (props) => {
    const tableRef = useRef();
    const { columns, classes, translations, permissions, activeCustomerID, getDebugLogs, activeCustomerName, resetSession } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, redirectPath: '' });

    const visible = columns && columns['RaspberryEraseStation'];

    const redirectHandler = (path) => {
        createHistory();
        setRedirectOptions({ redirect: true, redirectPath: path })
    };

    const CustomChip = props => {
        const { keyValue, value, className } = props;
        return (
            <Typography key={keyValue} className={className}>{value}</Typography>
        );
    };

    // const handleRevoke = async (data) => {
    //     await revokeRaspberryEraseStation({HardwareID: data.HardwareID, ActiveCustomerID: activeCustomerID });
    //     tableRef.current && tableRef.current.onQueryChange();
    // }

    const getLogs = async (rowData) => {
        const res = await getDebugLogs({RaspberryEraseStationID: rowData.EraseStationID, ActiveCustomerID: activeCustomerID });
        if (res) {
            snackActions.snackBar({
                message: translations["get_logs_action_success"] || "Action successful. Debug logs can be viewed in Work Dongle details",
                options: {
                    variant: 'success'
                }
            });
        }
    }

    const reset = async (rowData) => {
        await resetSession({RaspberryEraseStationID: rowData.EraseStationID, ActiveCustomerID: activeCustomerID });
        tableRef.current && tableRef.current.onQueryChange();
    }

    const goToProduct = (rowDataDetails) => {
        const activeCustomerParams = `&ActiveCustomerID=${activeCustomerID}&ActiveCustomerName=${activeCustomerName}`;
        let redirectURL = ``;
        switch (rowDataDetails?.ProductType) {
            case "PCProduct":
                redirectURL = `/pc-product/details?productID=${rowDataDetails.ProductID}&type=PCProduct`
                break;
            case "MobileProduct":
                redirectURL = `/mobile-product/details?productID=${rowDataDetails.ProductID}&type=MobileProduct`
                break;
            case "StorageProduct":
                redirectURL = `/storage-product/details?productID=${rowDataDetails.ProductID}&type=StorageProduct`
                break;
            case "WatchProduct":
                redirectURL = `/watch-product/details?productID=${rowDataDetails.ProductID}&type=WatchProduct`
                break;
            case "HeadphoneProduct":
                redirectURL = `/headphone-product/details?productID=${rowDataDetails.ProductID}&type=HeadphoneProduct`
                break;
            default:
                return;
        }
        redirectHandler(`${redirectURL}${activeCustomerParams}`);
    }

    const getStatusValue = (status) => {
        let eraseStatus;

        switch (status) {
            case "ready":
                eraseStatus = translations["ready"] || "Ready";
                break;
            case "starting":
                eraseStatus = translations["starting"] || "Starting";
                break;
            case "erasure_inprogress":
                eraseStatus = translations["in_progress"] || "In progress";
                break;
            case "analyze":
                eraseStatus = translations["analyzing_device_analyze"] || "Analyzing";
                break;
            case "completed":
                eraseStatus = translations["completed"] || "Completed";
                break;
            case "aborted":
                eraseStatus = translations["aborted"] || "Aborted";
                break;
            case "error":
                eraseStatus = translations["error"] || "Error";
                break;
            default:
                eraseStatus = translations["ready"] || "Ready";
        }
        return eraseStatus;
    }

    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["erase_status"] || 'Erase status', field: 'EraseStatus',
                hidden: false,
                editable: "never",
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.sessionStatus? getStatusValue(rowData.sessionStatus) : translations["ready"] || "Ready"}</Typography>;
                }
            },
            {
                title: translations["name"] || 'Name', field: 'Name',
                hidden: visible ? visible['Name'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations["short_not_assigned"] || "n/a"}</Typography>;
                }
            },
            {
                title: translations["hardware_id"] || 'Hardware ID', field: 'HardwareID',
                hidden: visible ? visible['HardwareID'] : false,
                filtering: true,
                editable: "never",
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.HardwareID ? rowData.HardwareID : translations["short_not_assigned"] || "n/a"}</Typography>;
                }
            },
            {
                title: translations["version"] || 'Version', field: 'Version',
                hidden: visible ? visible['Version'] : false,
                filtering: false,
                editable: "never",
                render: rowData => {
                    return <Typography variant={'caption'}>{isValid(rowData.MajorVersion) && isValid(rowData.MinorVersion) && isValid(rowData.BuildNumber) ? `${rowData.MajorVersion}.${rowData.MinorVersion}.${rowData.BuildNumber}` : 'n/a'}</Typography>;
                }
            },
            {
                title: translations["status"] || 'Status', field: 'Status',
                hidden: visible ? visible['Status'] : false,
                filtering: true,
                lookup: { 'inactive': translations["inactive"] || 'Inactive', 'active': translations["active"] || 'Active' },
                defaultFilter: ['inactive', 'active'],
                editable: "never",
                render: rowData => {
                    return <CustomChip key={`status_${rowData.EraseStationID}`} className={rowData.Status === 'active' ? 'active' : 'inactive'} value={rowData.Status === 'active' ? translations["active"] || "Active" : translations["inactive"] || "Inactive"} />;
                }
            },
            {
                title: translations["Online"] || 'Online', field: 'Online',
                hidden: visible ? visible['Online'] : false,
                filtering: true,
                lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
                defaultFilter: ['1', '0'],
                editable: "never",
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.SocketID ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            },
            {
                title: translations["securaze_id"] || 'SecurazeID', field: 'InventoryID',
                hidden: visible ? visible['InventoryID'] : false,
                filtering: true,
                editable: "never",
                render: rowData => {
                    let productDetails = null;
                    try {
                        productDetails = JSON.parse(rowData.Details);
                    } catch (e) {
                        productDetails = null;
                    }
                    return productDetails && productDetails.InventoryID ?
                        <Button color='primary' variant="contained" onClick={() => goToProduct(productDetails)}>{productDetails?.InventoryID}</Button> :
                        <Typography variant={'caption'}>{translations["short_not_assigned"] || "n/a"}</Typography>
                }
            }
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible, permissions]);

    const tableActions = [
        hasPermission(permissions, "raspberry_erase_station_load") ? {
            name: 'details',
            icon: FindInPage,
            tooltip: translations['raspberry_erase_station_details'] || 'Work Dongle details',
            onClick: (event, rowData) => redirectHandler(`/settings/raspberry-erase-station/edit?EraseStationID=${rowData.EraseStationID}`),
            disabled: false,
            position: "row"
        } : null,
        // hasPermission(permissions, 'raspberry_erase_station_revoke_license') && {
        //     name: 'revoke',
        //     icon: SettingsBackupRestore,
        //     tooltip: translations['revoke'] || 'Revoke',
        //     onClick: (event, rowData) => handleRevoke(rowData),
        //     disabled: false,
        //     position: "row"
        // },
        hasPermission(permissions, "raspberry_erase_station_get_debug_log") ? {
            name: 'get-debug-logs',
            icon: GetApp,
            tooltip: translations['get_debug_logs'] || 'Get debug logs',
            onClick: (event, rowData) => getLogs(rowData),
            disabled: false,
            position: "row"
        } : null,
        hasPermission(permissions, "raspberry_erase_station_reset_details") ? {
            name: 'reset',
            icon: RotateLeft,
            tooltip: translations['reset'] || 'Reset',
            onClick: (event, rowData) => reset(rowData),
            disabled: false,
            position: "row"
        } : null,
    ]

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'raspberry_erase_station_list') &&
                <Provider store={store}>
                    <RaspberryEraseStationList
                        tableRef={tableRef}
                        tableColumns={tableColumns}
                        activeCustomerID={activeCustomerID}
                        translations={translations}
                        columns={columns}
                        permissions={permissions}
                        tableActions={tableActions}
                    />
                </Provider>}
        </div>
    );
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        activeCustomerName: state.core.login.activeCustomer.Name,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
}

const mapDispatchToProps = dispatch => {
    return {
        revokeRaspberryEraseStation: (data) => dispatch(raspberryEraseStationActions.revokeRaspberryEraseStation(data)),
        getDebugLogs: (data) => dispatch(raspberryEraseStationActions.getDebugLogs(data)),
        resetSession: data => dispatch(raspberryEraseStationActions.resetRaspberryEraseStation(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(RaspberryEraseStation));